import { useEffect, useMemo, useState } from "react";
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis,
  CartesianGrid, Tooltip,
} from "recharts";
import { api } from "../lib/api";
import { Activity } from "lucide-react";

const METRICS = [
  { key: "distance_km", label: "Distance", unit: "km" },
  { key: "top_speed_kmh", label: "Top speed", unit: "km/h" },
  { key: "sprints", label: "Sprints", unit: "" },
  { key: "avg_heart_rate", label: "Avg HR", unit: "bpm" },
];

function shortDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return isNaN(d) ? "" : d.toLocaleDateString([], { month: "short", day: "numeric" });
}

function ChartTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const p = payload[0].payload;
  const metric = payload[0];
  return (
    <div className="bg-black border border-[#404040] rounded-sm px-3 py-2 text-xs">
      <div className="text-white font-semibold">
        {p.opponent ? `vs ${p.opponent}` : "Match"} {p.result ? `· ${p.result}` : ""}
      </div>
      <div className="text-[#a3a3a3] mt-0.5">{shortDate(p.date)}{p.season ? ` · ${p.season}` : ""}</div>
      <div className="text-white mt-1 font-display text-base">{metric.value}</div>
    </div>
  );
}

export default function FitnessTrends({ userId }) {
  const [data, setData] = useState(null);
  const [metric, setMetric] = useState("distance_km");
  const [season, setSeason] = useState("");

  useEffect(() => {
    const qs = season ? `?season=${encodeURIComponent(season)}` : "";
    api.get(`/users/${userId}/fitness${qs}`).then(({ data }) => setData(data)).catch(() => {});
  }, [userId, season]);

  const points = useMemo(
    () =>
      (data?.points || [])
        .filter((p) => typeof p[metric] === "number")
        .map((p) => ({ ...p, label: shortDate(p.date) })),
    [data, metric]
  );

  const availableMetrics = useMemo(
    () => METRICS.filter((m) => (data?.points || []).some((p) => typeof p[m.key] === "number")),
    [data]
  );

  if (!data || data.points.length === 0) return null;

  const meta = METRICS.find((m) => m.key === metric);
  const agg = data.averages?.[metric];

  return (
    <div className="bg-[#121212] border border-[#262626] rounded-sm p-5" data-testid="fitness-trends-section">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h2 className="font-display text-xl uppercase tracking-widest text-white flex items-center gap-2">
          <Activity className="w-5 h-5" />
          Fitness Trends
        </h2>
        {data.seasons.length > 0 && (
          <select
            data-testid="fitness-season-filter"
            value={season}
            onChange={(e) => setSeason(e.target.value)}
            className="bg-[#0a0a0a] border border-[#262626] focus:border-white outline-none px-3 py-1.5 rounded-sm text-xs text-white transition-colors"
          >
            <option value="">All seasons</option>
            {data.seasons.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        )}
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        {availableMetrics.map((m) => (
          <button
            key={m.key}
            type="button"
            data-testid={`fitness-metric-${m.key}`}
            onClick={() => setMetric(m.key)}
            className={`px-3 py-1.5 text-xs uppercase tracking-widest rounded-sm border transition-colors ${
              metric === m.key
                ? "bg-white text-black border-white font-semibold"
                : "bg-[#0a0a0a] text-[#a3a3a3] border-[#262626] hover:border-white hover:text-white"
            }`}
          >
            {m.label}
          </button>
        ))}
      </div>

      {points.length === 0 ? (
        <div className="text-sm text-[#737373] py-8 text-center">
          No {meta.label.toLowerCase()} data for this selection.
        </div>
      ) : (
        <>
          <div className="h-56" data-testid="fitness-chart">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={points} margin={{ top: 8, right: 12, bottom: 0, left: -18 }}>
                <CartesianGrid stroke="#262626" strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="label"
                  stroke="#404040"
                  tick={{ fill: "#737373", fontSize: 10 }}
                  tickLine={false}
                />
                <YAxis
                  stroke="#404040"
                  tick={{ fill: "#737373", fontSize: 10 }}
                  tickLine={false}
                  domain={["auto", "auto"]}
                />
                <Tooltip content={<ChartTooltip />} cursor={{ stroke: "#404040" }} />
                <Line
                  type="monotone"
                  dataKey={metric}
                  stroke="#ffffff"
                  strokeWidth={2}
                  dot={{ r: 3, fill: "#ffffff", stroke: "#121212", strokeWidth: 1.5 }}
                  activeDot={{ r: 5, fill: "#ffffff" }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
          {agg && (
            <div className="flex gap-2 mt-4">
              <div className="flex-1 bg-[#0a0a0a] border border-[#262626] p-3 text-center rounded-sm">
                <div data-testid="fitness-avg" className="font-display text-2xl text-white leading-none">
                  {agg.avg}
                </div>
                <div className="text-[10px] uppercase tracking-widest text-[#737373] mt-1">
                  Avg {meta.label} {meta.unit && `(${meta.unit})`}
                </div>
              </div>
              <div className="flex-1 bg-[#0a0a0a] border border-[#262626] p-3 text-center rounded-sm">
                <div data-testid="fitness-best" className="font-display text-2xl text-white leading-none">
                  {agg.best}
                </div>
                <div className="text-[10px] uppercase tracking-widest text-[#737373] mt-1">
                  Best {meta.label} {meta.unit && `(${meta.unit})`}
                </div>
              </div>
              <div className="flex-1 bg-[#0a0a0a] border border-[#262626] p-3 text-center rounded-sm">
                <div className="font-display text-2xl text-white leading-none">{points.length}</div>
                <div className="text-[10px] uppercase tracking-widest text-[#737373] mt-1">
                  Tracked matches
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
